import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { ThemeContext } from "../../context/ThemeContext";
import {
  fetchGuestReportReasons,
  isOtherReason,
  submitGuestDeviceReport,
} from "../../chat/reportDevice";

const isAbortError = (err) =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

const toastOpts = (theme) => ({
  className: theme ? "custom-toast" : "",
  bodyClassName: theme ? "custom-toast" : "",
  progressClassName: theme ? "custom-toast" : "",
});

const ReportDeviceSheet = ({ open, onClose, roomUuid, sessionId, lang }) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const abortRef = useRef(null);
  const submittingRef = useRef(false);

  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    setReasons([]);
    setSelectedId(null);
    setDetails("");
    setError(null);
    setSubmitting(false);
    submittingRef.current = false;
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    fetchGuestReportReasons(roomUuid, lang, { signal: controller.signal })
      .then((list) => {
        if (controller.signal.aborted) return;
        setReasons(list);
        if (!list.length) {
          setError(t("Could not load report reasons."));
        }
      })
      .catch((err) => {
        if (isAbortError(err) || controller.signal.aborted) return;
        setError(
          err?.response?.data?.message || t("Could not load report reasons."),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [open, roomUuid, lang, t]);

  const selectedReason = useMemo(
    () => reasons.find((reason) => String(reason.id) === String(selectedId)),
    [reasons, selectedId],
  );
  const otherSelected = isOtherReason(selectedReason);
  const detailsTrimmed = details.trim();
  const canSubmit =
    Boolean(selectedReason) &&
    (!otherSelected || detailsTrimmed.length > 0) &&
    !submitting &&
    !loading;

  const dismiss = () => {
    abortRef.current?.abort();
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current || !roomUuid || !sessionId) return;

    submittingRef.current = true;
    setSubmitting(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await submitGuestDeviceReport({
        roomUuid,
        sessionId,
        reasonId: selectedReason.id,
        additionalDetails: otherSelected ? detailsTrimmed : "",
        lang,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      toast.success(t("Report submitted successfully."), toastOpts(theme));
      onClose?.();
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) return;
      submittingRef.current = false;
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("Could not submit report. Please try again."),
      );
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="host-chat__report-overlay"
      role="presentation"
      onClick={dismiss}
    >
      <div
        className="host-chat__report-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-device-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="host-chat__report-sheet-close"
          aria-label={t("Close")}
          onClick={dismiss}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18 6L6 18M6 6l12 12"
            />
          </svg>
        </button>

        <h3 id="report-device-title" className="host-chat__report-sheet-title">
          {t("Report other device?")}
        </h3>
        <p className="host-chat__report-sheet-desc">
          {t("Select the reason that best describes what happened.")}
        </p>

        {loading ? (
          <div className="host-chat__report-sheet-status">
            {t("Loading...")}
          </div>
        ) : (
          <div className="host-chat__report-reasons" role="radiogroup">
            {reasons.map((reason) => {
              const selected = String(reason.id) === String(selectedId);
              return (
                <button
                  key={reason.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`host-chat__report-reason${selected ? " is-selected" : ""}`}
                  disabled={submitting}
                  onClick={() => setSelectedId(reason.id)}
                >
                  <span
                    className={`host-chat__report-radio${selected ? " is-selected" : ""}`}
                    aria-hidden="true"
                  />
                  <span className="host-chat__report-reason-label">
                    {reason.name ? t(reason.name) : t("Other")}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {otherSelected ? (
          <textarea
            className="host-chat__report-details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t("Please describe what happened...")}
            disabled={submitting}
            rows={4}
          />
        ) : null}

        {error ? (
          <div className="host-chat__report-sheet-error">{error}</div>
        ) : null}

        <button
          type="button"
          className="host-chat__report-submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? t("Submitting...") : t("Submit report")}
        </button>
      </div>
    </div>
  );
};

export default ReportDeviceSheet;
